import { safePercent } from "../../utils/format-number";
import StatNumber from "./stat-number";

type Props = { data: Record<string, any> | null };

type Segment = { label: string; value: number; bar: string; dot: string };

const n = (v: unknown) => Number(v ?? 0) || 0;

/**
 * Collections breakdown.
 *
 * Each row is a part-to-whole meter: one total, and the segments that compose it.
 * Segments are separated by a 2px gap in the surface colour rather than a stroke —
 * the gap does the separating without adding ink that isn't data.
 *
 * The old version set its sub-labels at 8px, which is below a readable floor; the
 * figures here sit at 12px and compact (£1.2M) with the exact amount on hover, so
 * rows stay aligned regardless of magnitude.
 */
export default function CollectionsPanel({ data }: Props) {
  const d = data ?? {};

  const rows: { key: string; head: string; total: number; segments: Segment[] }[] = [
    {
      key: "pre",
      head: "Collected by us · pre-handover",
      total: n(d.preHandoverViaAgent) + n(d.preHandoverViaPortal) + n(d.viaRobot),
      segments: [
        { label: "Agents", value: n(d.preHandoverViaAgent), bar: "bg-indigo-500", dot: "bg-indigo-500" },
        { label: "Portal", value: n(d.preHandoverViaPortal), bar: "bg-sky-500", dot: "bg-sky-500" },
        { label: "Robot", value: n(d.viaRobot), bar: "bg-amber-500", dot: "bg-amber-500" },
      ],
    },
    {
      key: "post",
      head: "Collected by us · post-handover",
      total: n(d.postHandoverViaIcb) + n(d.postHandoverViaPortal),
      segments: [
        { label: "ICB", value: n(d.postHandoverViaIcb), bar: "bg-indigo-500", dot: "bg-indigo-500" },
        { label: "Portal", value: n(d.postHandoverViaPortal), bar: "bg-sky-500", dot: "bg-sky-500" },
      ],
    },
    {
      key: "icb",
      head: "Fees charged by ICB",
      total: n(d.icbNewCharge) + n(d.icbOldCharge),
      segments: [
        { label: "Old charges", value: n(d.icbOldCharge), bar: "bg-indigo-500", dot: "bg-indigo-500" },
        { label: "New charges", value: n(d.icbNewCharge), bar: "bg-sky-500", dot: "bg-sky-500" },
      ],
    },
    {
      key: "chargeback",
      head: "Chargebacks",
      total: n(d.chargeBackSuccess) + n(d.chargeBackPending),
      segments: [
        { label: "Successful", value: n(d.chargeBackSuccess), bar: "bg-emerald-500", dot: "bg-emerald-500" },
        { label: "Pending", value: n(d.chargeBackPending), bar: "bg-amber-500", dot: "bg-amber-500" },
      ],
    },
  ];

  const net = rows.slice(0, 2).reduce((sum, r) => sum + r.total, 0);
  const fees = n(d.icbNewCharge) + n(d.icbOldCharge);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-6">
        {rows.map((row) => (
          <div key={row.key}>
            <div className="flex items-baseline justify-between gap-4 mb-2">
              <p className="text-[13px] font-medium text-text-secondary truncate">
                {row.head}
              </p>
              <p className="text-[14px] font-bold text-text-primary shrink-0 tabular-nums">
                <StatNumber value={row.total} currency />
              </p>
            </div>

            {/* 2px surface gaps between segments; the track shows through where empty. */}
            <div className="flex h-1.5 w-full rounded-full bg-overlay-mid overflow-hidden gap-[2px]">
              {row.segments.map((s) => (
                <div
                  key={s.label}
                  className={`h-full rounded-full transition-all duration-500 ${s.bar}`}
                  style={{ width: `${safePercent(s.value, row.total)}%` }}
                />
              ))}
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1">
              {row.segments.map((s) => (
                <div key={s.label} className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.dot}`} />
                  <span className="text-[12px] font-medium text-light-gray">
                    {s.label}
                  </span>
                  <span className="text-[12px] font-semibold text-text-primary tabular-nums">
                    <StatNumber value={s.value} currency />
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-edge-faint grid grid-cols-2 gap-3">
        <div>
          <p className="text-[11px] font-medium text-light-gray uppercase tracking-wide">
            Gross
          </p>
          <p className="mt-1 text-[17px] font-bold text-text-primary">
            <StatNumber value={net - fees} currency />
          </p>
        </div>
        <div className="text-right">
          <p className="text-[11px] font-medium text-light-gray uppercase tracking-wide">
            Net
          </p>
          <p className="mt-1 text-[17px] font-bold text-text-primary">
            <StatNumber value={net} currency />
          </p>
        </div>
      </div>
    </div>
  );
}
